//* Import needed modules
import { api } from "../../../services/apis/configs/axiosConfigs";
import { API_URI } from "../../../constants/consts";

//? Types for ApplicationStudents
import {
  type ApplicationStudent,
  type GetApplicationStudentsResponse,
  type GetApplicationStudentFullResponse,
  type CreateApplicationStudentInput,
  type ApplicationStudentDetails,
} from "./types/types";

//? Types for Pagination
import { type Pagination } from "../../../services/stores/types";

export const applicationStudentAPI = {
  /**
   * Get all ApplicationStudents within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param search - Termo de busca opcional.
   * @param page - Número da página para paginação.
   * @param token - Token de autenticação.
   * @param perPage - Número de itens por página.
   * @returns Uma promessa que resolve para GetApplicationStudentsResponse.
   */
  getAll: async function (
    applicationId: string,
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetApplicationStudentsResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/applications/${applicationId}/students?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination || {
          currentPage: 0,
          totalPages: 0,
          itemsPerPage: 0,
          totalItems: 0,
        };

        return {
          applicationStudents: data.data || [],
          pagination,
        };
      })
      .catch((error) => {
        console.error("Error fetching application students:", error);
        throw new Error("Failed to fetch application students");
      });
  },

  /**
   * Search for ApplicationStudents within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationStudent.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationStudentDetails.
   */
  getById: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<ApplicationStudentDetails> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/students/${id}`,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error fetching application student:", error);
        throw new Error("Failed to fetch application student");
      });
  },

  /**
   * Create a new ApplicationStudent within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param data - Dados para criar o ApplicationStudent.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationStudent.
   */
  create: async function (
    applicationId: string,
    data: CreateApplicationStudentInput,
    token: string
  ): Promise<ApplicationStudent> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/students`,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error creating application student:", error, data);
        throw error;
      });
  },

  /**
   * Fetches all students that are not in the application.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationStudent.
   * @param data - Dados para atualizar o ApplicationStudent.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para ApplicationStudent.
   */
  update: async function (
    applicationId: string,
    id: string,
    data: CreateApplicationStudentInput,
    token: string
  ): Promise<ApplicationStudent> {
    const payload = {
      ...data,
      application_id: applicationId,
    };

    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/students/${id}`,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: payload,
      })
      .then((response) => response.data.data)
      .catch((error) => {
        console.error("Error updating application student:", error);
        throw new Error("Failed to update application student");
      });
  },

  /**
   * Deletes an ApplicationStudent within an application.
   *
   * @param applicationId - ID da aplicação.
   * @param id - ID do ApplicationStudent.
   * @param token - Token de autenticação.
   * @returns Uma promessa que resolve para void.
   */
  delete: async function (
    applicationId: string,
    id: string,
    token: string
  ): Promise<void> {
    return api
      .request({
        url: `${API_URI}/admin/private/applications/${applicationId}/students/${id}`,
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(() => {})
      .catch((error) => {
        console.error("Error deleting application student:", error);
        throw new Error("Failed to delete application student");
      });
  },

  getAllFull: async function (
    applicationId: string,
    token: string
  ): Promise<GetApplicationStudentFullResponse> {
    const url = `${API_URI}/admin/private/applications/${applicationId}/students/full`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        // Supondo que response.data já seja o array de alunos
        const studentsArray = response.data;

        return {
          applicationStudent: studentsArray || [],
        } as GetApplicationStudentFullResponse;
      })
      .catch((error) => {
        console.error("Error fetching application students:", error);
        throw new Error("Failed to fetch application students");
      });
  },
};
